import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import {
  MAX_TOP_UP,
  REGISTRY_QUERY_KEY,
  creditsPath,
  draftFrom,
  fieldErrors,
  formatValue,
  isOwnerProtected,
  overridePath,
  overridesPath,
  parseDraft,
  type CreditsResponse,
  type FeatureValue,
  type OverridesResponse,
  type RegistryFeature,
  type TopUpResult,
  type ValueDraft,
  type WorkspaceOverride,
} from '../api/entitlements'
import { ConfirmDialog } from './ConfirmDialog'
import { ValueEditor } from './ValueEditor'
import '../views/screens.css'

interface WorkspaceRef {
  id: string
  name: string
}

interface RegistryResponse {
  features: RegistryFeature[]
  total: number
}

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

/** datetime-local value ("2026-12-01T09:30") for an ISO timestamp, in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ---------------------------------------------------------------------------
// Overrides
// ---------------------------------------------------------------------------

interface OverrideForm {
  key: string
  draft: ValueDraft
  expires: string // datetime-local; '' = never
  note: string
  existing: WorkspaceOverride | null
  error: string | null
}

interface PendingSet {
  key: string
  label: string
  value: FeatureValue
  expires_at: string | null
  note: string | null
  existing: WorkspaceOverride | null
}

export function OverridesPanel({ workspace }: { workspace: WorkspaceRef }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<OverrideForm | null>(null)
  const [pendingSet, setPendingSet] = useState<PendingSet | null>(null)
  const [pendingClear, setPendingClear] = useState<WorkspaceOverride | null>(null)
  const [typed, setTyped] = useState('')
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const listKey = ['admin-ws-overrides', workspace.id]

  const overrides = useQuery<OverridesResponse>({
    queryKey: listKey,
    queryFn: () => apiFetch<OverridesResponse>(overridesPath(workspace.id)),
  })
  useForbiddenGuard(overrides.error)
  const registry = useQuery<RegistryResponse>({
    queryKey: REGISTRY_QUERY_KEY,
    queryFn: () => apiFetch<RegistryResponse>('/admin/entitlements/registry'),
    staleTime: 5 * 60_000,
  })
  useForbiddenGuard(registry.error)
  const features = registry.data?.features ?? []
  const featureOf = (key: string) => features.find((f) => f.key === key) ?? null

  const setOverride = useMutation({
    mutationFn: (p: PendingSet) =>
      apiFetch(overridePath(workspace.id, p.key), {
        method: 'PUT',
        body: JSON.stringify({ value: p.value, note: p.note, expires_at: p.expires_at }),
      }),
    onSuccess: (_d, p) => {
      setForm(null)
      setOkMsg(`Override ${p.key} saved.`)
      void qc.invalidateQueries({ queryKey: listKey })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(setOverride.error)

  const clearOverride = useMutation({
    mutationFn: (o: WorkspaceOverride) => {
      const q = o.protected || isOwnerProtected(o.note) ? '?confirm_owner_protection=true' : ''
      return apiFetch(`${overridePath(workspace.id, o.key)}${q}`, { method: 'DELETE' })
    },
    onSuccess: (_d, o) => {
      setOkMsg(`Override ${o.key} cleared; the plan value applies again.`)
      void qc.invalidateQueries({ queryKey: listKey })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(clearOverride.error)

  const rows = overrides.data?.overrides ?? []
  const protectedCount = overrides.data?.protected ?? rows.filter((o) => o.protected).length
  const writeError = setOverride.error ?? clearOverride.error
  const errs = fieldErrors(writeError)

  function openAdd() {
    setOkMsg(null)
    setOverride.reset()
    const first = features.find((f) => !rows.some((o) => o.key === f.key)) ?? features[0]
    if (!first) return
    setForm({ key: first.key, draft: draftFrom(first, undefined), expires: '', note: '', existing: null, error: null })
  }

  function openEdit(o: WorkspaceOverride) {
    setOkMsg(null)
    setOverride.reset()
    const f = featureOf(o.key)
    setForm({
      key: o.key,
      draft: f ? draftFrom(f, o.value) : { raw: String(o.value ?? ''), unlimited: o.value === null },
      expires: toLocalInput(o.expires_at),
      note: o.note ?? '',
      existing: o,
      error: null,
    })
  }

  function submitForm() {
    if (!form) return
    const f = featureOf(form.key)
    if (!f) {
      setForm({ ...form, error: 'Unknown feature key.' })
      return
    }
    const parsed = parseDraft(f, form.draft)
    if (!parsed.ok) {
      setForm({ ...form, error: parsed.error })
      return
    }
    let expiresAt: string | null = null
    if (form.expires) {
      const d = new Date(form.expires)
      if (Number.isNaN(d.getTime())) {
        setForm({ ...form, error: 'Enter a valid expiry date.' })
        return
      }
      if (d.getTime() <= Date.now()) {
        setForm({ ...form, error: 'The expiry must be in the future.' })
        return
      }
      expiresAt = d.toISOString()
    }
    if (form.note.length > 500) {
      setForm({ ...form, error: 'The note is limited to 500 characters.' })
      return
    }
    setPendingSet({
      key: f.key,
      label: f.label,
      value: parsed.value,
      expires_at: expiresAt,
      note: form.note.trim() || null,
      existing: form.existing,
    })
  }

  const formFeature = form ? featureOf(form.key) : null
  const clearIsProtected = pendingClear
    ? pendingClear.protected || isOwnerProtected(pendingClear.note)
    : false

  return (
    <div className="scr-panel" aria-labelledby={`ovr-${workspace.id}`}>
      <h3 id={`ovr-${workspace.id}`}>Overrides</h3>
      <p className="scr-note">
        An override wins over the workspace&apos;s plan for one key until it expires or is
        cleared.
      </p>
      {protectedCount > 0 && (
        <p className="scr-badge scr-badge--denied" role="note">
          {protectedCount} owner-protection override{protectedCount === 1 ? '' : 's'} on this
          workspace. They keep the owner&apos;s limits; clearing one needs a typed confirmation.
        </p>
      )}
      {overrides.isLoading && <p className="scr-note">Loading overrides…</p>}
      {overrides.isError && (overrides.error as { status?: number }).status !== 403 && (
        <p className="scr-err" role="alert">
          Failed to load overrides.
        </p>
      )}
      {writeError && (
        <div className="scr-err" role="alert">
          <p>{writeErrorMessage(writeError)}</p>
          {errs.length > 0 && (
            <ul>
              {errs.map((e) => (
                <li key={e.key}>
                  <code>{e.key}</code>: {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      {overrides.isSuccess && (
        <table className="scr-table" aria-label="Overrides">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Value</th>
              <th>Expires</th>
              <th>Note</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => {
              const f = featureOf(o.key)
              const prot = o.protected || isOwnerProtected(o.note)
              return (
                <tr key={o.key} className={o.expired ? 'scr-row--muted' : undefined}>
                  <td>
                    {o.label ?? o.key}
                    <div>
                      <code className="scr-mono">{o.key}</code>
                    </div>
                    {prot && <span className="scr-badge scr-badge--denied">Owner protection</span>}
                    {o.expired && <span className="scr-badge">expired</span>}
                    {!o.registered && <span className="scr-badge">unregistered</span>}
                  </td>
                  <td>{formatValue(f, o.value)}</td>
                  <td>{o.expires_at ? fmtDate(o.expires_at) : 'Never'}</td>
                  <td>{o.note ?? '—'}</td>
                  <td>
                    <div className="scr-actions">
                      <button
                        type="button"
                        className="screen-btn"
                        aria-label={`Edit override ${o.key}`}
                        disabled={!o.registered || setOverride.isPending}
                        onClick={() => openEdit(o)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="screen-btn"
                        aria-label={`Clear override ${o.key}`}
                        disabled={clearOverride.isPending}
                        onClick={() => {
                          setOkMsg(null)
                          clearOverride.reset()
                          setTyped('')
                          setPendingClear(o)
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="ws-empty">
                  No overrides; the plan applies as is.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!form && (
        <div className="scr-panel-actions">
          <button
            type="button"
            className="screen-btn"
            disabled={features.length === 0}
            onClick={openAdd}
          >
            Add override
          </button>
        </div>
      )}

      {form && (
        <form
          noValidate
          className="scr-form"
          aria-label={form.existing ? `Edit override ${form.key}` : 'Add override'}
          onSubmit={(e) => {
            e.preventDefault()
            submitForm()
          }}
        >
          <label>
            Feature
            <select
              className="scr-input"
              aria-label="Feature"
              value={form.key}
              disabled={form.existing !== null}
              onChange={(e) => {
                const f = featureOf(e.target.value)
                setForm({ ...form, key: e.target.value, draft: f ? draftFrom(f, undefined) : form.draft, error: null })
              }}
            >
              {features.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label} ({f.key})
                </option>
              ))}
            </select>
          </label>
          {formFeature && (
            <label>
              Value
              <ValueEditor
                feature={formFeature}
                draft={form.draft}
                label="Value"
                onChange={(draft) => setForm({ ...form, draft, error: null })}
              />
            </label>
          )}
          <label>
            Expires (optional)
            <input
              className="scr-input"
              type="datetime-local"
              aria-label="Expires"
              value={form.expires}
              onChange={(e) => setForm({ ...form, expires: e.target.value, error: null })}
            />
          </label>
          <label>
            Note (optional)
            <input
              className="scr-input"
              type="text"
              aria-label="Note"
              maxLength={500}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value, error: null })}
            />
          </label>
          {form.error && (
            <span className="scr-err" role="alert">
              {form.error}
            </span>
          )}
          <div className="scr-actions">
            <button type="submit" className="screen-btn screen-btn-primary" disabled={setOverride.isPending}>
              Save override
            </button>
            <button type="button" className="screen-btn" onClick={() => setForm(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {pendingSet && (
        <ConfirmDialog
          label="Confirm override"
          onCancel={() => setPendingSet(null)}
          onConfirm={() => {
            const p = pendingSet
            setPendingSet(null)
            setOverride.mutate(p)
          }}
        >
          <p>
            Set <strong>{pendingSet.label}</strong> (<code>{pendingSet.key}</code>) to{' '}
            <strong>{formatValue(featureOf(pendingSet.key), pendingSet.value)}</strong> for{' '}
            <strong>{workspace.name}</strong>
            {pendingSet.expires_at ? `, until ${fmtDate(pendingSet.expires_at)}` : ', with no expiry'}?
          </p>
          {pendingSet.existing && (pendingSet.existing.protected || isOwnerProtected(pendingSet.existing.note)) && (
            <p className="scr-err">
              This replaces an owner-protection override. Keep the note starting with
              &quot;owner protection&quot; if it should stay protected.
            </p>
          )}
          <p className="scr-note">The workspace gets this value on its next request.</p>
        </ConfirmDialog>
      )}

      {pendingClear && (
        <ConfirmDialog
          label="Confirm clear override"
          confirmText="Clear override"
          confirmDisabled={clearIsProtected && typed.trim() !== pendingClear.key}
          onCancel={() => setPendingClear(null)}
          onConfirm={() => {
            const o = pendingClear
            setPendingClear(null)
            clearOverride.mutate(o)
          }}
        >
          <p>
            Clear the <strong>{pendingClear.label ?? pendingClear.key}</strong> override on{' '}
            <strong>{workspace.name}</strong>? The plan value applies again on the next request.
          </p>
          {clearIsProtected && (
            <>
              <p className="scr-err">
                This is an owner-protection override ({pendingClear.note}). Clearing it can lower
                the owner&apos;s limits. Type <code>{pendingClear.key}</code> to confirm.
              </p>
              <input
                className="scr-input"
                type="text"
                aria-label="Type the feature key to confirm"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            </>
          )}
        </ConfirmDialog>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------

export function CreditsPanel({ workspace }: { workspace: WorkspaceRef }) {
  const qc = useQueryClient()
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [pending, setPending] = useState<{ amount: number; note: string | null } | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const key = ['admin-ws-credits', workspace.id]

  const credits = useQuery<CreditsResponse>({
    queryKey: key,
    queryFn: () => apiFetch<CreditsResponse>(creditsPath(workspace.id)),
  })
  useForbiddenGuard(credits.error)

  const topUp = useMutation({
    mutationFn: (p: { amount: number; note: string | null }) =>
      apiFetch<TopUpResult>(creditsPath(workspace.id), {
        method: 'POST',
        body: JSON.stringify({ amount: p.amount, note: p.note }),
      }),
    onSuccess: (data, p) => {
      setAmount('')
      setNote('')
      setOkMsg(`Added ${p.amount.toLocaleString()} credits. Balance: ${data.balance.toLocaleString()}.`)
      qc.setQueryData<CreditsResponse>(key, (old) =>
        old ? { ...old, balance: data.balance, ledger: [data.entry, ...old.ledger] } : old,
      )
      void qc.invalidateQueries({ queryKey: key })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(topUp.error)

  const balance = credits.data?.balance ?? 0

  function submit() {
    setOkMsg(null)
    topUp.reset()
    const text = amount.trim()
    if (!/^\d+$/.test(text) || Number(text) < 1) {
      setFormError('Enter a whole number of credits, 1 or more.')
      return
    }
    const n = Number(text)
    if (n > MAX_TOP_UP) {
      setFormError(`At most ${MAX_TOP_UP.toLocaleString()} credits per top-up.`)
      return
    }
    setFormError(null)
    setPending({ amount: n, note: note.trim() || null })
  }

  return (
    <div className="scr-panel" aria-labelledby={`cr-${workspace.id}`}>
      <h3 id={`cr-${workspace.id}`}>Credits</h3>
      {credits.isLoading && <p className="scr-note">Loading credits…</p>}
      {credits.isError && (credits.error as { status?: number }).status !== 403 && (
        <p className="scr-err" role="alert">
          Failed to load credits.
        </p>
      )}
      {credits.isSuccess && (
        <p>
          Balance: <strong data-testid="credit-balance">{balance.toLocaleString()}</strong> credits
          <span className="scr-muted"> (drawn on only while the plan uses one-time credits)</span>
        </p>
      )}
      {topUp.isError && (
        <p className="scr-err" role="alert">
          {writeErrorMessage(topUp.error)}
        </p>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      <form
        noValidate
        className="scr-form"
        aria-label="Top up credits"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <label>
          Credits to add
          <input
            className="scr-input scr-input--narrow"
            type="number"
            min={1}
            step={1}
            aria-label="Credits to add"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value)
              setFormError(null)
            }}
          />
        </label>
        <label>
          Note (optional)
          <input
            className="scr-input"
            type="text"
            aria-label="Top-up note"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {formError && (
          <span className="scr-err" role="alert">
            {formError}
          </span>
        )}
        <div className="scr-actions">
          <button type="submit" className="screen-btn screen-btn-primary" disabled={topUp.isPending}>
            Top up
          </button>
        </div>
      </form>

      {credits.isSuccess && (
        <table className="scr-table" aria-label="Credit ledger">
          <thead>
            <tr>
              <th>When</th>
              <th>Reason</th>
              <th>Change</th>
              <th>Balance after</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {(credits.data?.ledger ?? []).map((e) => (
              <tr key={e.id}>
                <td>{fmtDate(e.created_at)}</td>
                <td>{e.reason}</td>
                <td>{e.delta > 0 ? `+${e.delta.toLocaleString()}` : e.delta.toLocaleString()}</td>
                <td>{e.balance_after.toLocaleString()}</td>
                <td>{e.note ?? '—'}</td>
              </tr>
            ))}
            {(credits.data?.ledger ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="ws-empty">
                  No ledger entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {pending && (
        <ConfirmDialog
          label="Confirm credit top-up"
          confirmText="Top up"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const p = pending
            setPending(null)
            topUp.mutate(p)
          }}
        >
          <p>
            Add <strong>{pending.amount.toLocaleString()}</strong> credits to{' '}
            <strong>{workspace.name}</strong>? The balance goes from {balance.toLocaleString()} to{' '}
            {(balance + pending.amount).toLocaleString()}.
          </p>
          <p className="scr-note">This writes a top_up ledger entry and an audit event.</p>
        </ConfirmDialog>
      )}
    </div>
  )
}
